// Khung ứng dụng: thanh bên theo danh mục module (nhóm theo tầng quy trình, số việc cần xử lý), thanh trên (vị trí,
// tìm nhanh, phần tử của module, việc cần xử lý, tạo mới, tài khoản), thanh dưới cho điện thoại.
// Không biết nghiệp vụ cụ thể của module: mọi điểm mở rộng đến từ pages/<m>/meta.ts qua props.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { APP_NAME, BRAND_LOGO_ON_DARK, BRAND_LOGO_ON_LIGHT, BRAND_MARK_ON_DARK, BRAND_MARK_ON_LIGHT } from '@/shared/config';
import { http, qk } from '@/shared/api';
import { cx, initials, pickLogo, useOverlay, useStoredState, useTheme } from '@/shared/lib';
import { Avatar, Btn, Empty, Icon, Menu, MenuItem, Modal, Tabs, ThemeSwitch, hasOpenLayer, useEscapeLayer, useFocusTrap } from '@/shared/ui';
import { MODULES, SECTION_LABEL, SECTION_LAYER, SECTION_ORDER, moduleByRoute } from '@/entities/tenant';
import { useCan, useViewer } from '@/entities/viewer';
import { ScopeTag, type MyPermissions } from '@/entities/permission';
import { session, useSsoConfig } from '@/entities/session';
import { InboxRow, inboxBadges, useInbox, type InboxItem } from '@/entities/inbox';
import { NotificationRow, useNotificationClock, useNotifications, useUnreadCount, type NotificationItem } from '@/entities/notification';
import { useMarkNotificationsRead } from '@/features/notification-mark-read';
import { useOpenInboxItem } from '@/features/inbox-open';
import { CommandPalette } from './CommandPalette';
import { permitted, type CreateItem, type SearchProvider, type TopbarItem } from './model';

export type { CreateItem, SearchHit, SearchContext, SearchProvider, TopbarItem } from './model';
export { paletteEntries, permitted } from './model';

const useVisible = () => {
  const b = useViewer();
  return useMemo(() => MODULES.filter((m) => b.visibleModules.includes(m.id)), [b.visibleModules]);
};
const currentRoute = (path: string) => path.split('/').filter(Boolean)[0] ?? '';
/** Bộ lọc dùng chung cho Tạo mới / Tìm nhanh / thanh trên: module hiển thị ∧ có quyền. */
function useAllowed() {
  const b = useViewer();
  const { any } = useCan();
  const visible = useCallback((id: string) => b.visibleModules.includes(id), [b.visibleModules]);
  return <T extends { module: string; perm?: string | string[] }>(xs: readonly T[]) => xs.filter((x) => permitted(x, visible, any));
}
const capped = (n: number) => (n > 99 ? '99+' : String(n));

/**
 * Đưa `el` vào vùng nhìn thấy của hộp cuộn `box` kiểu block 'nearest': đã nằm trọn ⇒ giữ nguyên; lệch trên ⇒ đỉnh mục chạm
 * đỉnh hộp; lệch dưới ⇒ đáy mục chạm đáy hộp (mục cao hơn hộp ⇒ canh đỉnh). Chỉ đổi box.scrollTop — khác scrollIntoView,
 * không bao giờ cuộn trang hay tổ tiên khác.
 */
function revealInBox(box: HTMLElement, el: HTMLElement) {
  const b = box.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  // Làm tròn ra ngoài: trình duyệt làm tròn scrollTop, toạ độ mục có phần lẻ (0,25 px) ⇒ không để mục hụt mép hộp
  if (r.top < b.top) box.scrollTop = Math.floor(box.scrollTop - (b.top - r.top));
  else if (r.bottom > b.bottom) box.scrollTop = Math.ceil(box.scrollTop + Math.min(r.bottom - b.bottom, r.top - b.top));
}

function SideBrand({ compact }: { compact: boolean }) {
  const b = useViewer();
  const { theme } = useTheme();
  // Logo công ty theo giao diện: sáng ⇒ logo thường; tối ⇒ bản nền tối, chưa có thì logo thường trên tấm trắng
  const logo = pickLogo(b.tenant, theme, { light: BRAND_LOGO_ON_LIGHT, dark: BRAND_LOGO_ON_DARK });
  if (logo.own)
    return (
      <div className="side-brand">
        <div className={cx('logo-plate', logo.plate && 'plate')} title={b.tenant.name}>
          <img src={logo.src} alt={b.tenant.name} />
        </div>
        {!compact && <div className="prod">{APP_NAME}</div>}
      </div>
    );
  return (
    <div className="side-brand">
      <div className="brand-row">
        <span className="logo logo-mark" title={b.tenant.name}>
          <img className="on-light" src={BRAND_MARK_ON_LIGHT} alt="" />
          <img className="on-dark" src={BRAND_MARK_ON_DARK} alt="" />
        </span>
        {!compact && (
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="co ellipsis">{b.tenant.name}</div>
            <div className="prod">{APP_NAME}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Sidebar({ open, onClose, collapsed, onToggle }: { open: boolean; onClose: () => void; collapsed: boolean; onToggle: () => void }) {
  const b = useViewer();
  const mods = useVisible();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const route = currentRoute(path);
  const { data: inbox } = useInbox();
  const counts = useMemo(() => inboxBadges(inbox, MODULES), [inbox]);
  // Thanh bên trượt (điện thoại / màn hẹp): mở ⇒ tiêu điểm vào liên kết đầu, Tab giữ trong thanh, Esc đóng và trả tiêu điểm
  // về nút đã mở; đóng ⇒ CSS ẩn hẳn (visibility) nên liên kết không còn trong thứ tự Tab
  const ref = useRef<HTMLElement>(null);
  useFocusTrap(ref, open, '.nav-item');
  useEscapeLayer(onClose, open);
  // Đổi trang (và lần dựng đầu, đổi danh sách module, thu gọn / mở rộng) ⇒ mục đang chọn luôn nằm trọn trong vùng cuộn .nav
  // (màn thấp như 1366×768 không chứa hết menu) — trước khi vẽ để không nháy
  const navRef = useRef<HTMLElement>(null);
  const reveal = useCallback(() => {
    const nav = navRef.current;
    const active = nav?.querySelector<HTMLElement>('.nav-item.active');
    if (nav && active) revealInBox(nav, active);
  }, []);
  useLayoutEffect(reveal, [reveal, route, mods, collapsed]);
  // Router bật scrollRestoration (app/router): .nav đã phát scroll thì router nhớ scrollTop và đặt lại (Back, tải lại) hoặc
  // chép sang trang mới ở onRendered — SAU layout effect trên ⇒ đưa mục vào tầm nhìn lần nữa sau đó. Listener chạy theo thứ
  // tự đăng ký, listener khôi phục của router đăng ký từ lúc tạo router nên luôn chạy trước. Đăng ký trong layout effect để
  // kịp lần vẽ đầu (onRendered phát ở microtask sau commit, trước passive effect). Chỉ khi đổi trang (hoặc lần vẽ đầu /
  // cùng địa chỉ): đổi search trong trang (mở drawer, đổi tab) giữ nguyên chỗ người dùng đã cuộn menu.
  const router = useRouter();
  useLayoutEffect(
    () =>
      router.subscribe('onRendered', (e) => {
        if (!e.fromLocation || !e.hrefChanged || currentRoute(e.fromLocation.pathname) !== currentRoute(e.toLocation.pathname)) reveal();
      }),
    [router, reveal],
  );
  return (
    <>
      {open && <div className="side-scrim" aria-hidden="true" onClick={onClose} />}
      <aside ref={ref} className={cx('side', open && 'open', collapsed && 'collapsed')} aria-label="Điều hướng chính">
        <SideBrand compact={collapsed} />
        <nav className="nav" ref={navRef}>
          {SECTION_ORDER.map((sec) => {
            const items = mods.filter((x) => x.section === sec).sort((a, b) => a.order - b.order);
            if (!items.length) return null;
            const layer = SECTION_LAYER[sec];
            return (
              <div className="nav-group" key={sec}>
                <div className="nav-layer" title={layer ? `Tầng ${layer} · ${SECTION_LABEL[sec]}` : SECTION_LABEL[sec]}>
                  <span className="lbl">{SECTION_LABEL[sec]}</span>
                </div>
                {items.map((x) => {
                  const n = counts[x.id] ?? 0;
                  return (
                    <Link
                      key={x.id}
                      to={`/${x.route}`}
                      className={cx('nav-item', route === x.route && 'active')}
                      title={collapsed ? x.label : undefined}
                      aria-current={route === x.route ? 'page' : undefined}
                      onClick={onClose}
                    >
                      <Icon n={x.icon} s={17} />
                      <span className="lbl">{x.label}</span>
                      {n > 0 && <span className="count">{capped(n)}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">
          <div className="me-card" title={collapsed ? `${b.account?.name} · ${b.role?.name ?? ''}` : undefined}>
            <Avatar id={b.account?.staffId ?? b.user.id} name={b.account?.name ?? b.user.displayName} />
            <div className="grow lbl" style={{ minWidth: 0 }}>
              <div className="strong ellipsis">{b.account?.name ?? b.user.displayName}</div>
              <div className="tiny ellipsis">{b.role?.name ?? 'Chưa gán vai trò'}</div>
            </div>
          </div>
          <button type="button" className="side-collapse" onClick={onToggle} aria-label={collapsed ? 'Mở rộng thanh bên' : 'Thu gọn thanh bên'}>
            <Icon n={collapsed ? 'right' : 'left'} s={15} />
            <span className="lbl">Thu gọn</span>
          </button>
        </div>
      </aside>
    </>
  );
}

type BellTab = 'inbox' | 'notifications';

/** Tab "Thông báo" của menu chuông: thông báo cá nhân mới nhất; bấm ⇒ đánh dấu đã đọc + mở liên kết. */
function NotificationPanel({ unread }: { unread: number }) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useNotifications();
  const now = useNotificationClock();
  const mark = useMarkNotificationsRead();
  const items = data?.items ?? [];
  const open = (n: NotificationItem) => {
    if (!n.readAt) mark.mutate({ ids: [n.id] });
    if (n.link?.to) void navigate({ to: n.link.to, search: (n.link.search ?? {}) as never });
  };
  return (
    <div role="tabpanel" aria-label="Thông báo">
      {unread > 0 && (
        <div className="menu-sub">
          <span className="muted small">{`${unread} chưa đọc`}</span>
          <Btn size="xs" v="ghost" icon="check" disabled={mark.isPending} onClick={() => mark.mutate({ all: true })}>
            Đánh dấu đã đọc
          </Btn>
        </div>
      )}
      {isLoading ? (
        <div className="menu-empty" role="status">
          Đang tải…
        </div>
      ) : isError ? (
        <div className="menu-empty">Chưa tải được thông báo.</div>
      ) : !items.length ? (
        <div className="menu-empty">Chưa có thông báo nào.</div>
      ) : (
        <div className="menu-scroll">
          {items.map((n) => (
            <NotificationRow key={n.id} item={n} now={now} onOpen={open} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Nội dung menu chuông (gắn mỗi lần mở): mở sẵn tab "Thông báo" khi có thông báo chưa đọc. */
function BellMenu({ items, count, total, unread }: { items: InboxItem[]; count: number; total: number; unread: number }) {
  const openItem = useOpenInboxItem();
  const [tab, setTab] = useState<BellTab>(() => (unread > 0 ? 'notifications' : 'inbox'));
  const open = (a: InboxItem) => void openItem(a);
  return (
    <>
      <div className="menu-tabs">
        <Tabs<BellTab>
          value={tab}
          onChange={setTab}
          tabs={[
            ['inbox', 'Việc cần xử lý', total || null],
            ['notifications', 'Thông báo', unread || null],
          ]}
        />
      </div>
      {tab === 'notifications' ? (
        <NotificationPanel unread={unread} />
      ) : (
        <div role="tabpanel" aria-label="Việc cần xử lý">
          {count > 0 && (
            <div className="menu-sub">
              <span className="muted small">{`${count} mục`}</span>
            </div>
          )}
          {!items.length ? (
            <div className="menu-empty">Không có việc tồn đọng.</div>
          ) : (
            <div className="menu-scroll">
              {items.map((a) => (
                <InboxRow key={a.id} item={a} onOpen={open} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Chuông: hộp "Việc cần xử lý" do notification-service tổng hợp theo người dùng (sắp bad → warn → violet → info) + tab
 * "Thông báo" (thông báo cá nhân). Chấm số = việc tồn đọng + thông báo chưa đọc.
 */
function Bell() {
  const { data } = useInbox();
  const unread = useUnreadCount();
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const count = data?.count ?? items.length;
  const dot = total + unread;
  const label = `Việc cần xử lý: ${total}${unread ? ` · Thông báo chưa đọc: ${unread}` : ''}`;
  return (
    <Menu
      align="right"
      width={400}
      trigger={(toggle, o) => (
        <button type="button" className="icon-btn" aria-label={label} aria-expanded={o} onClick={toggle}>
          <Icon n="bell" s={18} />
          {dot > 0 && (
            <span className="dot-count" aria-hidden="true">
              {dot > 99 ? '99+' : dot}
            </span>
          )}
        </button>
      )}
    >
      <BellMenu items={items} count={count} total={total} unread={unread} />
    </Menu>
  );
}

/** Bấm mục Tạo mới: mở lớp phủ tại trang hiện tại (overlay) hoặc điều hướng tới trang của module. */
function useRunCreate() {
  const navigate = useNavigate();
  const overlay = useOverlay();
  return (c: CreateItem) => (c.overlay ? overlay.open(c.overlay[0], c.overlay[1]) : void navigate({ to: c.to, search: c.search as never }));
}

/**
 * Phím tắt một chữ của mục Tạo mới (VD N ⇒ form khách): không kèm phím bổ trợ, không gõ trong ô nhập, không có lớp nổi
 * nào đang mở (modal, drawer, menu, Tìm nhanh). Chỉ mục đã lọc quyền + module hiển thị.
 */
function useCreateShortcuts(items: CreateItem[]) {
  const runCreate = useRunCreate();
  const latest = useRef({ items, runCreate });
  latest.current = { items, runCreate };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.repeat || e.isComposing || e.defaultPrevented || hasOpenLayer()) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || t.closest('input, textarea, select, [contenteditable]'))) return;
      const key = e.key.toLowerCase();
      const c = latest.current.items.find((x) => x.shortcut?.toLowerCase() === key);
      if (!c) return;
      e.preventDefault();
      latest.current.runCreate(c);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

function CreateMenu({ items }: { items: CreateItem[] }) {
  const runCreate = useRunCreate();
  if (!items.length) return null;
  return (
    <Menu
      align="right"
      width={240}
      trigger={(toggle, o) => (
        <Btn icon="plus" aria-expanded={o} onClick={toggle}>
          {/* Điện thoại: chữ ẩn nhưng vẫn là tên của nút (chỉ còn biểu tượng +) */}
          <span className="sr-sm">Tạo mới</span>
        </Btn>
      )}
    >
      {items.map((c) => (
        <MenuItem key={`${c.module}:${c.label}`} icon={c.icon} hint={c.hint ?? c.shortcut?.toUpperCase()} onClick={() => runCreate(c)}>
          {c.label}
        </MenuItem>
      ))}
    </Menu>
  );
}

function AccountMenu({ onPerms }: { onPerms: () => void }) {
  const b = useViewer();
  const navigate = useNavigate();
  // Đăng nhập qua Elite Accounts ⇒ lối sang trang Accounts (trang chủ Accounts chính là danh sách "Ứng dụng") ở tab mới
  const { data: sso } = useSsoConfig();
  return (
    <Menu
      align="right"
      width={280}
      trigger={(toggle, o) => (
        <button type="button" className="avatar-btn" aria-label="Tài khoản" aria-expanded={o} onClick={toggle}>
          <Avatar id={b.account?.staffId ?? b.user.id} name={b.account?.name ?? b.user.displayName} />
        </button>
      )}
    >
      <div className="menu-head col" style={{ alignItems: 'flex-start', gap: 2 }}>
        <span className="strong">{b.account?.name ?? b.user.displayName}</span>
        <span className="muted small">
          {b.role?.name ?? 'Chưa gán vai trò'}
          {b.account?.code ? ` · ${b.account.code}` : ''}
        </span>
        <span className="muted tiny">{b.user.email}</span>
      </div>
      <div className="menu-sub">
        <span className="small muted">Giao diện</span>
        <ThemeSwitch />
      </div>
      <div className="menu-sep" />
      <MenuItem icon="shield" onClick={onPerms}>
        Quyền của tôi
      </MenuItem>
      <MenuItem icon="search" hint="Ctrl K" onClick={() => window.dispatchEvent(new CustomEvent('cmdk'))}>
        Tìm nhanh
      </MenuItem>
      {sso?.enabled && (
        <MenuItem icon="layers" onClick={() => void window.open(sso.accountsUrl, '_blank', 'noopener')}>
          Tài khoản Elite
        </MenuItem>
      )}
      <div className="menu-sep" />
      <MenuItem
        icon="logout"
        onClick={async () => {
          await session.logout();
          void navigate({ to: '/dang-nhap' });
        }}
      >
        Đăng xuất
      </MenuItem>
    </Menu>
  );
}

/** Phần tử nhỏ của module trên thanh trên (đã lọc module hiển thị + quyền). */
function TopbarItems({ items }: { items: TopbarItem[] }) {
  return (
    <>
      {items.map(({ id, component: C }) => (
        <C key={id} />
      ))}
    </>
  );
}

function Topbar({ onMenu, onPerms, createItems, topbarItems }: { onMenu: () => void; onPerms: () => void; createItems: CreateItem[]; topbarItems: TopbarItem[] }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const mod = moduleByRoute(currentRoute(path));
  return (
    <header className="topbar">
      <button type="button" className="icon-btn menu-btn" onClick={onMenu} aria-label="Mở menu">
        <Icon n="menu" s={18} />
      </button>
      <div className="crumbs" aria-label="Vị trí">
        <span className="hide-sm">{mod ? SECTION_LABEL[mod.section] : ''}</span>
        <Icon n="right" s={12} className="hide-sm crumb-sep" />
        <span className="cur">{mod?.label ?? ''}</span>
      </div>
      <button type="button" className="search-trigger" aria-keyshortcuts="Control+K Meta+K" onClick={() => window.dispatchEvent(new CustomEvent('cmdk'))}>
        <Icon n="search" s={15} />
        {/* Điện thoại: chỉ còn biểu tượng — chữ ẩn bằng .sr-sm nên nút vẫn có tên */}
        <span className="sr-sm">Tìm khách, mã căn, giao dịch…</span>
        <kbd className="hide-sm" aria-hidden="true">
          Ctrl K
        </kbd>
      </button>
      <div className="topbar-r">
        <TopbarItems items={topbarItems} />
        <Bell />
        <CreateMenu items={createItems} />
        <AccountMenu onPerms={onPerms} />
      </div>
    </header>
  );
}

function BottomNav({ onMenu }: { onMenu: () => void }) {
  const mods = useVisible()
    .filter((m) => m.mobileTab)
    .sort((a, b) => a.order - b.order)
    .slice(0, 4);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const route = currentRoute(path);
  return (
    <nav className="bottom-nav" aria-label="Điều hướng nhanh">
      {mods.map((m) => (
        <Link key={m.id} to={`/${m.route}`} className={route === m.route ? 'on' : ''} aria-current={route === m.route ? 'page' : undefined}>
          <Icon n={m.icon} s={20} />
          {m.mobileTab}
        </Link>
      ))}
      <button type="button" onClick={onMenu}>
        <Icon n="menu" s={20} />
        Thêm
      </button>
    </nav>
  );
}

/** Quyền hiệu lực của tài khoản đang đăng nhập (theo nhóm module). */
export function MyPermissionsModal({ onClose }: { onClose: () => void }) {
  const b = useViewer();
  const { data } = useQuery({ queryKey: qk.viewer.permissions(), queryFn: () => http.get<MyPermissions>('/me/permissions') });
  return (
    <Modal size="lg" title="Quyền của tôi" sub={`${b.account?.name ?? b.user.displayName} · ${b.role?.name ?? 'Chưa gán vai trò'}`} onClose={onClose} foot={<Btn onClick={onClose}>Đóng</Btn>}>
      <div className="col gap-12">
        {(data?.groups ?? []).map((g) => (
          <div key={g.module}>
            <div className="sec-title">
              <span>{g.label}</span>
            </div>
            <ul className="perm-list">
              {g.items.map((p) => (
                <li key={p.key}>
                  <Icon n="check" s={14} />
                  <span className="grow">{p.label}</span>
                  <ScopeTag value={p.scope} />
                </li>
              ))}
            </ul>
          </div>
        ))}
        {data && !data.groups.length && <Empty title="Chưa có quyền nào" />}
      </div>
    </Modal>
  );
}

const NONE: never[] = [];

export function AppShell({
  children,
  createItems = NONE,
  searchProviders = NONE,
  topbarItems = NONE,
}: {
  children: ReactNode;
  createItems?: CreateItem[];
  searchProviders?: SearchProvider[];
  topbarItems?: TopbarItem[];
}) {
  const [menu, setMenu] = useState(false);
  const [perms, setPerms] = useState(false);
  const [collapsed, setCollapsed] = useStoredState('cathouse.side', false);
  const mods = useVisible();
  const allowed = useAllowed();
  const creates = allowed(createItems);
  const providers = allowed(searchProviders);
  const bar = allowed(topbarItems);
  useCreateShortcuts(creates);
  return (
    <>
      <div className={cx('app', collapsed && 'side-collapsed')}>
        {/* Điểm Tab đầu tiên: bỏ qua menu + thanh trên, đưa tiêu điểm vào nội dung trang */}
        <a
          className="skip-link"
          href="#main"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('main')?.focus();
          }}
        >
          Bỏ qua tới nội dung
        </a>
        <Sidebar open={menu} onClose={() => setMenu(false)} collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
        <div className="main">
          <Topbar onMenu={() => setMenu(true)} onPerms={() => setPerms(true)} createItems={creates} topbarItems={bar} />
          <main className="page" id="main" tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>
      <BottomNav onMenu={() => setMenu(true)} />
      <CommandPalette modules={mods} createItems={creates} providers={providers} />
      {perms && <MyPermissionsModal onClose={() => setPerms(false)} />}
    </>
  );
}

export function NoAccess() {
  const navigate = useNavigate();
  return (
    <div className="panel" style={{ marginTop: 8 }}>
      <Empty title="Bạn chưa có quyền xem mục này" action={<Btn onClick={() => navigate({ to: '/tong-quan' })}>Về Tổng quan</Btn>}>
        Nhờ quản trị viên cấp quyền trong Tài khoản & quyền.
      </Empty>
    </div>
  );
}
